import { useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { History, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import api from '@/lib/api';
import { historyAction, historyEntity, historyField, historyValue, isHiddenHistoryKey } from './settingsHistoryLabels';

// Configuration-related entities whose changes belong in the settings history.
const CONFIG_ENTITIES = [
  'Tenant',
  'TaxRuleSettings',
  'TaxRate',
  'Location',
  'Currency',
  'Role',
  'Brand',
  'PriceTier',
  'LoyaltySettings',
  'Webhook',
  'ApiKey',
  'PaymentTerminal',
  'TenantBackup',
].join(',');

interface AuditUser {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

interface AuditRow {
  id: string;
  action: string;
  entity: string;
  entityId?: string | null;
  createdAt: string;
  userId?: string | null;
  user?: AuditUser | null;
  oldValues?: Record<string, unknown> | null;
  newValues?: Record<string, unknown> | null;
}

const ACTION_STYLE: Record<string, string> = {
  CREATE: 'bg-success/10 text-success-text',
  UPDATE: 'bg-info/10 text-info-text',
  DELETE: 'bg-destructive/10 text-destructive-text',
  SET_DEFAULT: 'bg-warning/10 text-warning-text',
  REVOKE: 'bg-destructive/10 text-destructive-text',
  RESTORE: 'bg-primary/10 text-primary-text',
};

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function ChangeDiff({ row }: { row: AuditRow }) {
  const { t } = useTranslation('settings');
  const oldV = row.oldValues ?? {};
  const newV = row.newValues ?? {};
  const keys = Array.from(new Set([...Object.keys(oldV), ...Object.keys(newV)]))
    .filter((k) => !isHiddenHistoryKey(k, (newV as Record<string, unknown>)[k] ?? (oldV as Record<string, unknown>)[k]))
    .filter((k) => fmt((oldV as Record<string, unknown>)[k]) !== fmt((newV as Record<string, unknown>)[k]));

  if (keys.length === 0) {
    return <p className="text-xs text-muted-foreground py-2">{t('historyNoFieldDetail')}</p>;
  }

  return (
    <div className="space-y-1 py-2">
      {keys.map((k) => (
        <div key={k} className="grid grid-cols-[11rem_1fr] gap-2 text-xs">
          <span className="font-medium text-foreground break-words" title={historyField(t, k)}>{historyField(t, k)}</span>
          <span className="text-muted-foreground break-all">
            <span className="text-destructive-text line-through me-2">{historyValue(t, (oldV as Record<string, unknown>)[k])}</span>
            <span className="text-success-text">{historyValue(t, (newV as Record<string, unknown>)[k])}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export function SettingsHistoryCard() {
  const { t } = useTranslation('settings');
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['settings-history'],
    queryFn: () =>
      api
        .get(`/audit-logs?entities=${encodeURIComponent(CONFIG_ENTITIES)}&limit=25`)
        .then((r) => r.data),
    staleTime: 30_000,
  });

  const rows: AuditRow[] = data?.data ?? [];

  return (
    <Card className="animate-fade-in-up hover:shadow-glow-sm transition-all duration-200 lg:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <History className="h-5 w-5 text-muted-foreground" />
          {t('settingsHistoryTitle')}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{t('settingsHistorySub')}</p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonTable rows={5} cols={4} />
        ) : rows.length === 0 ? (
          <EmptyState icon={History} title={t('settingsHistoryEmpty')} />
        ) : (
          <div className="overflow-x-auto">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('settingsHistoryWhen')}</Th>
                  <Th>{t('settingsHistoryWho')}</Th>
                  <Th>{t('settingsHistoryAction')}</Th>
                  <Th>{t('settingsHistoryEntity')}</Th>
                  <Th />
                </Tr>
              </THead>
              <TBody>
                {rows.map((row) => {
                  const isOpen = expanded === row.id;
                  const who = row.user
                    ? `${row.user.firstName ?? ''} ${row.user.lastName ?? ''}`.trim() || (row.user.email ?? '—')
                    : row.userId
                      ? t('historyUnknownUser')
                      : t('historySystem');
                  return (
                    <Fragment key={row.id}>
                      <Tr
                        className="cursor-pointer"
                        onClick={() => setExpanded(isOpen ? null : row.id)}
                      >
                        <Td label={t('settingsHistoryWhen')} className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(row.createdAt)}</Td>
                        <Td label={t('settingsHistoryWho')} className="text-xs">{who}</Td>
                        <Td label={t('settingsHistoryAction')}>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_STYLE[row.action] ?? 'bg-muted text-muted-foreground'}`}>
                            {historyAction(t, row.action)}
                          </span>
                        </Td>
                        <Td label={t('settingsHistoryEntity')} className="text-xs font-medium">{historyEntity(t, row.entity)}</Td>
                        <Td className="text-muted-foreground">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Td>
                      </Tr>
                      {isOpen && (
                        <Tr hover={false} className="bg-muted/20">
                          <Td colSpan={5}>
                            <ChangeDiff row={row} />
                          </Td>
                        </Tr>
                      )}
                    </Fragment>
                  );
                })}
              </TBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
